import { useState } from "react";
import { Link } from "react-router-dom";

export default function B2BContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    inquiryType: "",
    description: "",
    website_url: "" // Honeypot field for anti-spam
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
    submissionId: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null, submissionId: null });

    // Client side email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({
        submitting: false,
        submitted: false,
        error: "Indtast venligst en gyldig e-mailadresse.",
        submissionId: null
      });
      return;
    }

    if (!formData.inquiryType) {
      setStatus({
        submitting: false,
        submitted: false,
        error: "Vælg venligst en transporttype.",
        submissionId: null
      });
      return;
    }

    try {
      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus({
          submitting: false,
          submitted: true,
          error: null,
          submissionId: result.submissionId
        });
        setFormData({
          name: "",
          company: "",
          phone: "",
          email: "",
          inquiryType: "",
          description: "",
          website_url: ""
        });
      } else {
        setStatus({
          submitting: false,
          submitted: false,
          error: result.message || "Der opstod en fejl ved afsendelse. Prøv venligst igen.",
          submissionId: null
        });
      }
    } catch {
      setStatus({
        submitting: false,
        submitted: false,
        error: "Netværksfejl. Kunne ikke forbinde til serveren. Prøv venligst igen senere.",
        submissionId: null
      });
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 lg:p-10 shadow-xl relative overflow-hidden text-slate-800">
      {/* Header & Qualification */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/10 border border-[#2DD4BF]/30 text-[#0F766E] text-xs font-mono uppercase tracking-widest mb-3">
          <i className="fa-solid fa-building text-[10px] text-[#0F766E]" aria-hidden="true" />
          <span>Erhvervshenvendelse · B2B Samarbejde</span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-slate-950 tracking-tight">
          Drøft et fast transportbehov
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Vi hjælper virksomheder med faste og tilbagevendende transportbehov i hele Danmark inkl. Bornholm. Udfyld formularen, så vender vi tilbage med et uforpligtende oplæg.
        </p>
      </div>

      {status.submitted ? (
        <div className="p-8 rounded-xl bg-teal-50 border border-teal-200 text-center">
          <div className="w-14 h-14 bg-[#0F766E] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl shadow-lg shadow-[#0F766E]/20">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-bold text-slate-950">Tak for din henvendelse</h4>
          <p className="text-slate-600 text-sm mt-2 max-w-md mx-auto leading-relaxed">
            Vi har modtaget din forespørgsel og gennemgår jeres transportbehov. Vi svarer normalt inden for én hverdag.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, submitted: false, error: null, submissionId: null })}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider transition cursor-pointer"
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - strictly hidden with sr-only and absolute positioning */}
          <div className="sr-only absolute -left-[9999px] top-0 pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave empty)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                Kontaktperson *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Anders Jensen"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition placeholder:text-slate-400"
              />
            </div>

            {/* Virksomhed */}
            <div>
              <label htmlFor="company" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                Virksomhed / Organisation
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="F.eks. Pharma A/S eller Food ApS"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition placeholder:text-slate-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Telefon */}
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                Telefonnummer *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 12 34 56 78"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition placeholder:text-slate-400"
              />
            </div>

            {/* E-mail */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
                E-mailadresse *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="kontakt@virksomhed.dk"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Emne / type */}
          <div>
            <label htmlFor="inquiryType" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
              Hvad drejer henvendelsen sig om? *
            </label>
            <div className="relative">
              <select
                id="inquiryType"
                name="inquiryType"
                required
                value={formData.inquiryType}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition appearance-none cursor-pointer"
              >
                <option value="">Vælg transporttype</option>
                <option value="Medicinaltransport">Medicinaltransport</option>
                <option value="Køletransport">Køletransport</option>
                <option value="Fødevaretransport">Fødevaretransport</option>
                <option value="Fast transportaftale">Fast transportaftale / underleverandør</option>
                <option value="Andet erhvervssamarbejde">Andet erhvervssamarbejde</option>
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 text-xs">
                <i className="fa-solid fa-chevron-down" aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">
              Beskrivelse af transportbehov *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows={4}
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskriv jeres faste ruter, type gods, særlige krav til køl eller ønsket samarbejdsmodel..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-[#0F766E] focus:bg-white focus:ring-1 focus:ring-[#0F766E] transition placeholder:text-slate-400 resize-y"
            ></textarea>
          </div>

          {status.error && (
            <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation text-sm flex-shrink-0" aria-hidden="true" />
              <span>{status.error}</span>
            </div>
          )}

          <div className="text-xs text-slate-500 leading-relaxed">
            Ved afsendelse behandler vi dine indtastede oplysninger med henblik på at besvare din henvendelse. Læs mere i vores{" "}
            <Link to="/privatlivspolitik" className="text-[#0F766E] hover:underline font-semibold">
              privatlivspolitik
            </Link>
            .
          </div>

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-lg bg-[#0F766E] hover:bg-[#115E59] active:bg-[#134E4A] text-white text-sm font-semibold tracking-wide transition shadow-md shadow-[#0F766E]/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin text-sm" aria-hidden="true" />
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send forespørgsel</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
